import { SampledEngine, SOUND_PROFILES } from './engine.js';

// The selection screen revs once and then stays quiet, so the rev is the same
// idle-to-throttle the grid uses, driven for a single burst. EngineDrive shapes
// a rev around 1.25s of its own timeline and a second one around 2.75s, so the
// body opens on idle and closes before the second rev begins. The tail then
// holds that idle while a gain recedes, because dropping the engine the frame
// the body ends is what made the rev sound cut off.
const REV_LEAD = .55;
const REV_BODY = 1.45;
const REV_TAIL = 1.2;
const REV_RESTORE = .25;
// iOS Safari starts a context suspended and only lets it resume inside a
// gesture, and its versions disagree on which of these counts as that gesture.
const GESTURES = ['pointerdown', 'touchend', 'click', 'keydown'];

function clickBuffer(context) {
  const buffer = context.createBuffer(1, Math.ceil(context.sampleRate * .1), context.sampleRate);
  const data = buffer.getChannelData(0);
  let state = 2463534242;
  for (let index = 0; index < data.length; index++) {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    data[index] = state / 2147483648 - 1;
  }
  return buffer;
}

export class RaceAudio {
  constructor({ profile = 'apex', onError = () => {} } = {}) {
    this.profile = Object.hasOwn(SOUND_PROFILES, profile) ? profile : null;
    this.onError = onError;
    this.sampledEngine = null;
    this.engineLoading = null;
    this.click = null;
    this.revUntil = 0;
    this.revActive = false;
    this.revArmed = false;
    this.revSpent = false;
    this.gesture = null;
    this.stage = '';
    this.enabled = false;
    this.context = null;
    this.lastStage = '';
    this.lastLamp = -1;
    this.pausedTracks = new Set();
    this.champion = new Audio('build/champion.mp3');
    this.champion.preload = 'none'; this.champion.volume = .52;
    this.cheer = new Audio('build/crowd.mp3');
    this.cheer.preload = 'none'; this.cheer.volume = .22;
    this.finishCuesRequested = false;
  }

  ensureContext() {
    if (this.context) return this.context;
    const Constructor = window.AudioContext || window.webkitAudioContext;
    if (!Constructor) return null;
    this.context = new Constructor();
    this.master = this.context.createGain(); this.master.gain.value = 0;
    this.master.connect(this.context.destination);
    // The engine reaches the master through its own gain so the entry rev can
    // recede under it while the race itself keeps the engine at full level.
    this.engineLevel = this.context.createGain(); this.engineLevel.gain.value = 1;
    this.engineLevel.connect(this.master);
    if (!this.profile) this.createLegacyEngine();
    return this.context;
  }

  // The paddock greets the player with one throttle rev. The samples are the
  // ones the race itself will use, so this front-loads bytes the round needs
  // anyway rather than adding an asset of its own.
  async prepareSelection() {
    const context = this.ensureContext();
    if (!context) return;
    this.setEnabled(true);
    // Never awaited: a browser holding the context until a gesture keeps this
    // promise pending, and waiting on it would stop the samples loading at all.
    context.resume().catch(() => {});
    if (!this.profile) return;
    if (!this.sampledEngine) {
      this.engineLoading ??= SampledEngine.load(context, this.profile)
        .then(buffers => { this.sampledEngine = new SampledEngine(context, this.engineLevel, this.profile, buffers); })
        .catch(error => { this.onError(error); })
        .finally(() => { this.engineLoading = null; });
    }
    // Armed before the samples land, so a tap that arrives early still counts.
    this.armRev();
    await this.engineLoading;
    this.startRev();
  }

  // Returns false when the browser is holding the context back, in which case
  // the rev waits for the player's first touch instead of never sounding at all.
  startRev() {
    if (this.revSpent || !this.sampledEngine || this.context.state !== 'running') return false;
    this.revSpent = true;
    this.disarmRev();
    const now = this.context.currentTime;
    this.revActive = true;
    this.revUntil = now + REV_BODY + REV_TAIL;
    const gain = this.engineLevel.gain;
    gain.cancelScheduledValues(now);
    gain.setValueAtTime(1, now);
    gain.setValueAtTime(1, now + REV_BODY);
    gain.setTargetAtTime(0, now + REV_BODY, REV_TAIL / 3);
    // Back to full level once the engine has already gone quiet, so whatever the
    // player does next starts from an untouched engine.
    gain.setValueAtTime(1, now + REV_BODY + REV_TAIL + REV_RESTORE);
    return true;
  }

  // A gesture that the browser refuses leaves the other listeners in place: on
  // iOS a touch pointerdown is often not the event that unlocks audio, while the
  // touchend or click that follows it is.
  armRev() {
    if (this.revArmed || this.revSpent) return;
    this.revArmed = true;
    this.gesture = () => {
      this.context.resume().then(async () => {
        if (this.revSpent || this.stage !== 'selecting') return;
        await this.engineLoading;
        this.startRev();
      }).catch(() => {});
    };
    for (const type of GESTURES) window.addEventListener(type, this.gesture, { capture: true, once: true });
  }

  disarmRev() {
    if (!this.revArmed) return;
    this.revArmed = false;
    for (const type of GESTURES) window.removeEventListener(type, this.gesture, { capture: true });
    this.gesture = null;
  }

  endRev() {
    if (!this.revArmed && !this.revActive) return;
    this.disarmRev();
    // Leaving the selection screen spends the entry rev: it belongs to arriving,
    // not to a later tap from the grid.
    this.revSpent = true;
    if (!this.revActive) return;
    this.revActive = false; this.revUntil = 0;
    const gain = this.engineLevel.gain;
    gain.cancelScheduledValues(this.context.currentTime);
    gain.setValueAtTime(1, this.context.currentTime);
  }

  // A car change lands as a short mechanical tick and nothing else, so the
  // selection screen never turns into a bed of sound.
  selectionBlip() {
    if (!this.context || !this.enabled) return;
    const now = this.context.currentTime;
    this.click ??= clickBuffer(this.context);
    const source = this.context.createBufferSource(); source.buffer = this.click;
    const tone = this.context.createBiquadFilter(); tone.type = 'highpass'; tone.frequency.value = 1900;
    const envelope = this.context.createGain();
    envelope.gain.setValueAtTime(.022, now);
    envelope.gain.exponentialRampToValueAtTime(.0001, now + .06);
    source.connect(tone); tone.connect(envelope); envelope.connect(this.master);
    source.onended = () => { tone.disconnect(); envelope.disconnect(); };
    source.start(now, 0, .1);
  }

  // The finish cues only sound once the field is racing, so they stay off the
  // first screen and start downloading when a round begins. A play() on a
  // loading track still starts it as soon as enough data arrives.
  loadFinishCues() {
    if (this.finishCuesRequested) return;
    this.finishCuesRequested = true;
    for (const track of [this.champion, this.cheer]) { track.preload = 'auto'; track.load(); }
  }

  async unlock() {
    if (!this.ensureContext()) return;
    this.setEnabled(true);
    // Not awaited, for the same reason as the selection screen: a context the
    // browser is holding until a gesture would otherwise block the samples.
    this.context.resume().catch(() => {});
    if (this.profile && !this.sampledEngine && !this.engineLoading) {
      this.engineLoading = SampledEngine.load(this.context, this.profile).then(buffers => {
        this.sampledEngine = new SampledEngine(this.context, this.engineLevel, this.profile, buffers);
      }).catch(error => { this.onError(error); }).finally(() => { this.engineLoading = null; });
    }
    await this.engineLoading;
  }

  createLegacyEngine() {
    this.engineGain = this.context.createGain(); this.engineGain.gain.value = 0;
    const filter = this.context.createBiquadFilter(); filter.type = 'lowpass'; filter.frequency.value = 950;
    this.engineGain.connect(filter); filter.connect(this.master);
    this.engine = this.context.createOscillator(); this.engine.type = 'sawtooth'; this.engine.frequency.value = 60;
    this.engine.connect(this.engineGain); this.engine.start();
    this.harmonic = this.context.createOscillator(); this.harmonic.type = 'triangle'; this.harmonic.frequency.value = 122;
    const harmonicGain = this.context.createGain(); harmonicGain.gain.value = .28;
    this.harmonic.connect(harmonicGain); harmonicGain.connect(this.engineGain); this.harmonic.start();
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (this.context) this.master.gain.setTargetAtTime(enabled ? (this.profile ? .82 : .34) : 0, this.context.currentTime, .05);
    this.champion.muted = !enabled; this.cheer.muted = !enabled;
  }

  beep(frequency = 640, length = .10, gain = .12) {
    if (!this.context || !this.enabled) return;
    const oscillator = this.context.createOscillator();
    const envelope = this.context.createGain();
    const now = this.context.currentTime;
    oscillator.frequency.value = frequency;
    envelope.gain.setValueAtTime(gain * (this.profile ? .34 / .82 : 1), now);
    envelope.gain.exponentialRampToValueAtTime(.001, now + length);
    oscillator.connect(envelope); envelope.connect(this.master);
    oscillator.start(); oscillator.stop(now + length);
  }

  update(stage, elapsed, sample, selectedCarId) {
    this.stage = stage;
    if (!this.context) return;
    const now = this.context.currentTime;
    const car = sample?.cars.find(item => item.id === selectedCarId);
    const running = stage === 'racing';
    const preparing = stage === 'grid' || stage === 'countdown';
    const frequency = running ? 72 + (car?.speed || 0) * 3.2 + Math.sin(elapsed * 17) * 5 : preparing ? 53 + Math.max(0, elapsed - 2) * 12 : 42;
    if (this.sampledEngine) this.sampledEngine.update(this.#engineFrame(stage, elapsed, car, now), now);
    if (!this.profile) {
      this.engine.frequency.setTargetAtTime(frequency, now, .10);
      this.harmonic.frequency.setTargetAtTime(frequency * 2.01, now, .10);
      this.engineGain.gain.setTargetAtTime(running ? .11 : preparing ? .065 : 0, now, .12);
    }
    const lamp = stage === 'countdown' ? Math.min(5, Math.floor((elapsed - 2) / .47) + 1) : 0;
    if (lamp && lamp !== this.lastLamp) this.beep(480, .08, .07);
    this.lastLamp = lamp;
    if (stage !== this.lastStage) {
      if (stage !== 'selecting') this.endRev();
      if (stage === 'grid' || stage === 'countdown' || stage === 'racing') this.loadFinishCues();
      if (stage === 'racing') { this.beep(980, .24, .12); if (this.enabled) { this.cheer.currentTime = 0; this.cheer.play().catch(() => {}); } }
      if (stage === 'celebrating' && this.enabled) { this.champion.currentTime = 0; this.champion.play().catch(() => {}); }
      if (stage === 'selecting') { this.champion.pause(); this.cheer.pause(); }
      this.lastStage = stage;
    }
  }

  // While the entry rev is running it drives the engine instead of the stage, so
  // the paddock gets one burst of throttle and the race that follows is
  // untouched. Through the tail it holds the engine at the point the body ended
  // and lets the engine gain recede.
  #engineFrame(stage, elapsed, car, now) {
    if (this.revActive) {
      if (this.revUntil > now) {
        const position = Math.min(REV_BODY, REV_BODY + REV_TAIL - (this.revUntil - now));
        return { stage: 'grid', elapsed: REV_LEAD + position, speed: 0, acceleration: 0 };
      }
      this.revActive = false;
    }
    return { stage, elapsed, speed: car?.speed || 0, acceleration: car?.acceleration || 0 };
  }

  pause() {
    if (this.context) this.context.suspend().catch(() => {});
    for (const track of [this.champion, this.cheer]) {
      if (!track.paused && !track.ended) this.pausedTracks.add(track);
      track.pause();
    }
  }

  resume() {
    if (this.context) this.context.resume().catch(() => {});
    if (this.enabled) for (const track of this.pausedTracks) track.play().catch(() => {});
    this.pausedTracks.clear();
  }
}
